/**
 * AdminPricingSettings — live pricing constants for AI uploads.
 *
 * Admin-only. Loads current settings + defaults from
 * GET /api/upload-pricing/settings, shows a form with every constant,
 * previews the resulting price against a sample upload, and saves back
 * via PUT /api/upload-pricing/settings. Reset button restores defaults.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTruthState } from "../hooks/useTruthState";
import { TruthReceipt } from "../components/TruthReceipt";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Save, RotateCcw, Loader2, Sparkles, Calculator,
  History, SlidersHorizontal, User as UserIcon,
  Eye, CreditCard, TrendingUp, Crown, Video, Clock3,
} from "lucide-react";
import useSafeBack from "../hooks/useSafeBack";

const API = process.env.REACT_APP_BACKEND_URL;

const FIELD_GROUPS = [
  {
    title: "Core pricing",
    fields: [
      { key: "base_fee_pence",             label: "Base fee",                 unit: "pence", hint: "Applied to every upload." },
      { key: "duration_fee_per_min_pence", label: "Duration fee per minute",  unit: "pence", hint: "Charged per rounded-up minute of video." },
      { key: "size_fee_per_mb_tenths",     label: "Size fee per MB",          unit: "tenths of a penny", hint: "2 = £0.002/MB. 10 = £0.01/MB." },
      { key: "premium_addon_pence",        label: "Premium Deep add-on",      unit: "pence", hint: "Added on top of standard for premium analysis." },
    ],
  },
  {
    title: "Video save add-on",
    fields: [
      { key: "video_save_tier_1_pence", label: "Tier 1 — small videos",  unit: "pence", hint: "Applied when file ≤ tier-1 max MB." },
      { key: "video_save_tier_1_max_mb", label: "Tier 1 max size",       unit: "MB",    hint: "Default 500 MB." },
      { key: "video_save_tier_2_pence", label: "Tier 2 — medium videos", unit: "pence", hint: "Applied when tier-1 max < size ≤ tier-2 max." },
      { key: "video_save_tier_2_max_mb", label: "Tier 2 max size",       unit: "MB",    hint: "Default 1000 MB." },
      { key: "video_save_tier_3_pence", label: "Tier 3 — large videos",  unit: "pence", hint: "Applied when size > tier-2 max." },
    ],
  },
  {
    title: "Safety caps",
    fields: [
      { key: "min_charge_pence", label: "Minimum charge", unit: "pence", hint: "Never charge less than this." },
      { key: "max_charge_pence", label: "Maximum charge", unit: "pence", hint: "Never charge more than this." },
    ],
  },
];

// Live in-browser preview using the same formula (mirrors backend compute_quote).
const ceil = Math.ceil;
const roundUpTo99 = (p) => {
  if (p <= 99) return 99;
  const pounds = Math.floor(p / 100);
  const boundary = pounds * 100 + 99;
  return p <= boundary ? boundary : (pounds + 1) * 100 + 99;
};
function previewQuote(s, durationSec, sizeBytes) {
  const dMin = ceil(durationSec / 60);
  const sMb = ceil(sizeBytes / (1024 * 1024));
  const dFee = dMin * s.duration_fee_per_min_pence;
  const szFee = ceil((sMb * s.size_fee_per_mb_tenths) / 10);
  const rawStd = s.base_fee_pence + dFee + szFee;
  const rawPrem = rawStd + s.premium_addon_pence;
  const clamp = (p) => Math.min(s.max_charge_pence, Math.max(s.min_charge_pence, roundUpTo99(p)));
  let vAdd = s.video_save_tier_3_pence;
  if (sMb <= s.video_save_tier_1_max_mb) vAdd = s.video_save_tier_1_pence;
  else if (sMb <= s.video_save_tier_2_max_mb) vAdd = s.video_save_tier_2_pence;
  return { std: clamp(rawStd), prem: clamp(rawPrem), video: vAdd };
}
const money = (p) => `£${(p / 100).toFixed(2)}`;

export default function AdminPricingSettings() {
  useSafeBack();
  const [tab, setTab] = useState("settings"); // settings | history
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [form, setForm] = useState({});
  const [note, setNote] = useState("");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Batch P1 · Operations Truth-State — canonical async receipt.
  // Pricing save is a LOCAL authoritative mutation (settings row is
  // committed on backend return) so `completed` is legitimate here.
  const truth = useTruthState();
  useEffect(() => {
    if (truth.state === "completed") {
      const t = setTimeout(() => truth.setState(null), 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [truth]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/upload-pricing/settings`, { withCredentials: true });
      setSettings(data.settings);
      setDefaults(data.defaults);
      setForm(data.settings);
    } catch (e) {
      truth.setState("failed", e.response?.data?.detail || "Failed to load settings.");
    }
    setLoading(false);
  };
  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/upload-pricing/settings/history?limit=100`, { withCredentials: true });
      setHistory(data.items || []);
    } catch (e) {
      truth.setState("failed", e.response?.data?.detail || "Failed to load history.");
    }
    setHistoryLoading(false);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === "history") loadHistory(); }, [tab]);

  const dirty = useMemo(() => {
    if (!settings) return false;
    return Object.keys(settings).some((k) => String(form[k] ?? "") !== String(settings[k] ?? ""));
  }, [form, settings]);

  const sampleSmall = previewQuote(form, 5 * 60, 10 * 1024 * 1024);
  const sampleMid   = previewQuote(form, 42 * 60 + 30, 800 * 1024 * 1024);
  const sampleLarge = previewQuote(form, 90 * 60, Math.round(1.5 * 1024 * 1024 * 1024));

  const onChange = (key) => (e) => {
    const v = e.target.value.replace(/[^\d]/g, "");
    setForm({ ...form, [key]: v === "" ? 0 : parseInt(v, 10) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (note.trim()) payload.note = note.trim();
      const { data } = await axios.put(`${API}/api/upload-pricing/settings`, payload, { withCredentials: true });
      setSettings(data.settings);
      setForm(data.settings);
      setNote("");
      truth.setState("completed", data.changes_logged > 0 ? `Saved — ${data.changes_logged} change(s) logged.` : "Saved.");
    } catch (e) {
      truth.setState("failed", e.response?.data?.detail || "Save failed.");
    }
    setSaving(false);
  };

  const reset = async () => {
    if (!window.confirm("Reset pricing to defaults? This will affect all new uploads immediately.")) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/upload-pricing/settings/reset`, {}, { withCredentials: true });
      setSettings(data.settings);
      setForm(data.settings);
      truth.setState("completed", "Reset to defaults.");
    } catch (e) {
      truth.setState("failed", e.response?.data?.detail || "Reset failed.");
    }
    setSaving(false);
  };

  if (loading || !settings) {
    return (
      <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 pb-20">
      <header className="sticky top-0 z-40 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Link to="/admin" className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="back-to-admin">
            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="font-extrabold text-slate-900 dark:text-white leading-tight">Upload Pricing</h1>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Live formula · applies to all new AI uploads</p>
          </div>
          <button
            data-testid="pricing-reset-btn"
            onClick={reset}
            disabled={saving}
            className="h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-700 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-1.5 disabled:opacity-60"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-4 space-y-4">
        <TruthReceipt
          state={truth.state}
          message={truth.message}
          onRetry={load}
          onDismiss={() => truth.setState(null)}
        />
        {/* Tab strip */}
        <div
          role="tablist"
          className="inline-flex rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-1"
          data-testid="pricing-tabs"
        >
          <TabButton label="Settings" icon={SlidersHorizontal} active={tab === "settings"} onClick={() => setTab("settings")} testid="tab-settings" />
          <TabButton label={`History${history.length ? ` · ${history.length}` : ""}`} icon={History} active={tab === "history"} onClick={() => setTab("history")} testid="tab-history" />
        </div>

        {tab === "settings" && <>
        {/* Live preview */}
        <section
          data-testid="pricing-preview"
          className="rounded-2xl border border-indigo-200 dark:border-indigo-800 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-900/20 dark:to-violet-900/20 p-4"
        >
          <div className="flex items-center gap-2 mb-3">
            <Calculator className="w-4 h-4 text-indigo-600" />
            <h2 className="text-[11px] font-extrabold uppercase tracking-wider text-indigo-700 dark:text-indigo-300">
              Live preview
            </h2>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <PreviewRow label="5m · 10MB"        q={sampleSmall} />
            <PreviewRow label="42m30s · 800MB"   q={sampleMid} />
            <PreviewRow label="90m · 1.5GB"      q={sampleLarge} />
          </div>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-2 text-center">
            STD = standard review · PREM = premium deep · VID = video-save add-on
          </p>
        </section>

        {/* Field groups */}
        {FIELD_GROUPS.map((group) => (
          <section
            key={group.title}
            className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4"
          >
            <h3 className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">
              {group.title}
            </h3>
            <div className="space-y-3">
              {group.fields.map((f) => (
                <FieldRow
                  key={f.key}
                  field={f}
                  value={form[f.key] ?? 0}
                  defaultValue={defaults?.[f.key]}
                  onChange={onChange(f.key)}
                />
              ))}
            </div>
          </section>
        ))}

        {/* Optional note for audit log */}
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4">
          <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400 block mb-2">
            Reason / note <span className="text-slate-400">(optional — attached to audit log)</span>
          </label>
          <input
            data-testid="pricing-note-input"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 500))}
            placeholder="e.g. Raising base fee for 2-week A/B test"
            className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Save bar */}
        <div className="sticky bottom-4 z-30 flex gap-2 justify-end">
          <button
            data-testid="pricing-save-btn"
            onClick={save}
            disabled={saving || !dirty}
            className="h-12 px-5 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white font-extrabold shadow-xl disabled:opacity-40 flex items-center gap-2 active:scale-[0.99] transition-transform"
          >
            {saving
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
              : <><Save className="w-4 h-4" /> {dirty ? "Save changes" : "Saved"}</>}
          </button>
        </div>

        <p className="text-[10.5px] text-center text-slate-400 flex items-center justify-center gap-1">
          <Sparkles className="w-3 h-3" /> Changes apply to the next upload — no redeploy.
        </p>
        </>}

        {tab === "history" && (
          <HistoryPanel items={history} loading={historyLoading} onRefresh={loadHistory} />
        )}
      </main>
    </div>
  );
}

function TabButton({ label, icon: Icon, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors ${
        active
          ? "bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900"
          : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
      }`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function HistoryPanel({ items, loading, onRefresh }) {
  return (
    <section data-testid="pricing-history-panel" className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-extrabold text-slate-900 dark:text-white">Change log</h2>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">Append-only. Most recent first.</p>
        </div>
        <button
          data-testid="history-refresh"
          onClick={onRefresh}
          disabled={loading}
          className="h-8 px-2.5 rounded-lg border border-slate-200 dark:border-slate-700 text-[11px] font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Refresh"}
        </button>
      </div>

      {loading && items.length === 0 && (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-6 text-center text-slate-400 text-xs">
          <Loader2 className="w-4 h-4 animate-spin inline-block mr-1" /> Loading…
        </div>
      )}
      {!loading && items.length === 0 && (
        <div data-testid="history-empty" className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-8 text-center">
          <History className="w-8 h-8 mx-auto text-slate-300 mb-2" />
          <p className="text-sm font-bold text-slate-600 dark:text-slate-300">No pricing changes yet</p>
          <p className="text-[11px] text-slate-400 mt-1">The log starts filling up after your first save.</p>
        </div>
      )}

      {items.map((it) => <HistoryRow key={it.id} item={it} />)}
    </section>
  );
}

function HistoryRow({ item }) {
  const date = new Date(item.created_at);
  const dateLabel = date.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  const changes = Object.entries(item.changes || {});
  const isReset = item.action === "reset";
  return (
    <article
      data-testid={`history-item-${item.id}`}
      className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden"
    >
      <div className="px-4 py-3 flex items-start gap-3 border-b border-slate-100 dark:border-slate-800">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isReset
            ? "bg-rose-100 text-rose-600 dark:bg-rose-900/25 dark:text-rose-300"
            : "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/25 dark:text-indigo-300"
        }`}>
          {isReset ? <RotateCcw className="w-4 h-4" /> : <SlidersHorizontal className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-extrabold text-slate-900 dark:text-white">
            {isReset ? "Reset to defaults" : `${changes.length} field${changes.length === 1 ? "" : "s"} changed`}
          </p>
          <p className="text-[10.5px] text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mt-0.5 truncate">
            <UserIcon className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{item.admin_email || item.admin_name || "unknown admin"}</span>
            <span className="text-slate-300">·</span>
            <span>{dateLabel}</span>
          </p>
          {item.note && (
            <p className="text-[11px] text-slate-600 dark:text-slate-300 mt-1.5 italic bg-slate-50 dark:bg-slate-800 rounded-md px-2 py-1 inline-block max-w-full">
              “{item.note}”
            </p>
          )}
        </div>
      </div>
      <div className="px-4 py-2 divide-y divide-slate-100 dark:divide-slate-800">
        {changes.length === 0 && (
          <p className="text-[11px] text-slate-400 py-1.5">No effective changes recorded.</p>
        )}
        {changes.map(([k, v]) => (
          <div key={k} className="py-1.5 flex items-center justify-between gap-2 text-[11px]">
            <span className="font-bold text-slate-700 dark:text-slate-200 truncate">{k}</span>
            <span className="flex items-center gap-1.5 flex-shrink-0 font-mono">
              <span className="text-slate-400 line-through">{v.old ?? "—"}</span>
              <span className="text-slate-300">→</span>
              <span className="text-emerald-600 dark:text-emerald-400 font-bold">{v.new ?? "—"}</span>
            </span>
          </div>
        ))}
      </div>

      <ConversionOverlay metrics={item.metrics_48h} windowComplete={item.metrics_window_complete} />
    </article>
  );
}

function ConversionOverlay({ metrics, windowComplete }) {
  if (!metrics) return null;
  const {
    quotes_shown, paid_uploads, conversion_pct,
    revenue_pence, premium_upgrades, video_save_uploads,
  } = metrics;
  const empty = quotes_shown === 0 && paid_uploads === 0;
  return (
    <div data-testid="conversion-overlay" className="px-4 py-3 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
          <TrendingUp className="w-3 h-3" />
          48-hour window
        </p>
        {!windowComplete && (
          <span data-testid="window-in-progress" className="inline-flex items-center gap-1 text-[9.5px] font-bold text-amber-600 dark:text-amber-400">
            <Clock3 className="w-2.5 h-2.5" /> in progress
          </span>
        )}
      </div>
      {empty ? (
        <p className="text-[11px] text-slate-400 italic">No uploads in this window yet.</p>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          <Stat icon={Eye}          label="Quotes shown" value={quotes_shown} />
          <Stat icon={CreditCard}   label="Paid" value={paid_uploads} accent="emerald" />
          <Stat icon={TrendingUp}   label="Conv. %" value={`${conversion_pct}%`} accent="indigo" />
          <Stat icon={Sparkles}     label="Revenue" value={`£${(revenue_pence / 100).toFixed(2)}`} accent="emerald" span />
          <Stat icon={Crown}        label="Premium" value={premium_upgrades} accent="amber" />
          <Stat icon={Video}        label="Video save" value={video_save_uploads} accent="violet" />
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent = "slate", span = false }) {
  const tone = {
    slate:   "text-slate-700 dark:text-slate-200",
    emerald: "text-emerald-600 dark:text-emerald-400",
    indigo:  "text-indigo-600 dark:text-indigo-400",
    amber:   "text-amber-600 dark:text-amber-400",
    violet:  "text-violet-600 dark:text-violet-400",
  }[accent] || "text-slate-700";
  return (
    <div className={`rounded-lg bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-slate-700/60 px-2 py-1.5 ${span ? "col-span-1" : ""}`}>
      <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1">
        <Icon className="w-2.5 h-2.5" /> {label}
      </p>
      <p className={`text-xs font-extrabold mt-0.5 ${tone}`}>{value}</p>
    </div>
  );
}

function PreviewRow({ label, q }) {
  return (
    <div className="rounded-xl bg-white/70 dark:bg-slate-900/40 border border-white/60 dark:border-slate-800 p-2">
      <p className="text-[10px] text-slate-500 font-bold truncate">{label}</p>
      <p className="text-[11px] font-extrabold text-slate-900 dark:text-white mt-1">STD {money(q.std)}</p>
      <p className="text-[11px] font-bold text-amber-700 dark:text-amber-400">PREM {money(q.prem)}</p>
      <p className="text-[10px] text-violet-600 dark:text-violet-400">+VID {money(q.video)}</p>
    </div>
  );
}

function FieldRow({ field, value, defaultValue, onChange }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <label className="block text-sm font-bold text-slate-800 dark:text-slate-100">
          {field.label}
          <span className="ml-1.5 text-[10px] font-semibold text-slate-400 uppercase">{field.unit}</span>
        </label>
        <p className="text-[10.5px] text-slate-500 dark:text-slate-400 leading-snug mt-0.5">
          {field.hint}
          {defaultValue !== undefined && defaultValue !== value && (
            <span className="ml-1 text-slate-400">· default {defaultValue}</span>
          )}
        </p>
      </div>
      <input
        data-testid={`pricing-field-${field.key}`}
        value={value}
        onChange={onChange}
        inputMode="numeric"
        className="w-24 h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-right text-sm font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
    </div>
  );
}
